import { useCallback, useEffect, useState } from "react";
import { Download, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, downloadExcel } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

export default function StockHistory() {
  const [date, setDate] = useState(istToday());
  const [productId, setProductId] = useState("all");
  const [warehouseId, setWarehouseId] = useState("all");
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pr, wh] = await Promise.all([API.get("/products"), API.get("/warehouses")]);
        setProducts(pr.data.filter((p) => (p.type || "product") !== "service"));
        setWarehouses(wh.data);
      } catch {
        toast.error("Could not load filters");
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const r = await API.get("/inventory/stock-history", {
        params: {
          date,
          product_id: productId === "all" ? "" : productId,
          warehouse_id: warehouseId === "all" ? "" : warehouseId,
        },
      });
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load stock history");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [date, productId, warehouseId]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async () => {
    if (downloading || !report) return;
    setDownloading(true);
    try {
      const q = `date=${date}${productId !== "all" ? `&product_id=${productId}` : ""}${warehouseId !== "all" ? `&warehouse_id=${warehouseId}` : ""}`;
      await downloadExcel(`/inventory/stock-history/export?${q}`, `stock-as-of-${date}.xlsx`);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const showCost = !!report?.show_cost;
  const rows = report?.rows || [];
  const totalQty = rows.reduce((a, r) => a + r.qty, 0);
  const totalValue = rows.reduce((a, r) => a + (r.value || 0), 0);

  return (
    <div className="space-y-5" data-testid="stock-history-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2.5 font-heading text-3xl font-bold text-slate-900">
            <History className="h-7 w-7 text-indigo-600" /> Stock History
          </h1>
          <p className="mt-1 text-sm text-slate-500">Stock as of any past date, reconstructed from every transaction up to that day</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={download} disabled={!report || loading || downloading} data-testid="sh-download-button">
          {downloading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
          Download Excel
        </Button>
      </div>

      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3" data-testid="sh-filters">
        <div>
          <Label>Stock as of date</Label>
          <Input type="date" value={date} max={istToday()} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="sh-date-input" />
        </div>
        <div>
          <Label>Item</Label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger className="mt-1.5" data-testid="sh-product-select"><SelectValue placeholder="All items" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="sh-product-option-all">All items</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`sh-product-option-${p.id}`}>{p.name}{p.sku ? ` (${p.sku})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Warehouse</Label>
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger className="mt-1.5" data-testid="sh-warehouse-select"><SelectValue placeholder="All warehouses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="sh-warehouse-option-all">All warehouses</SelectItem>
              {warehouses.map((w) => (
                <SelectItem key={w.id} value={w.id} data-testid={`sh-warehouse-option-${w.id}`}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="sh-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Reconstructing stock...
        </div>
      ) : !report ? null : rows.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <EmptyState title="No stock as of this date" hint="No non-zero balances found for the selected filters." />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">Cartons</TableHead>
                {showCost && <TableHead className="text-right">Avg Cost</TableHead>}
                {showCost && <TableHead className="text-right">Stock Value</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody data-testid="sh-table">
              {rows.map((r) => (
                <TableRow key={`${r.product_id}-${r.warehouse_id}`} data-testid={`sh-row-${r.product_id}-${r.warehouse_id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-400">{r.sku || ""}</div>
                  </TableCell>
                  <TableCell data-testid={`sh-warehouse-${r.product_id}-${r.warehouse_id}`}>{r.warehouse}</TableCell>
                  <TableCell className={`text-right font-medium ${r.qty < 0 ? "text-rose-600" : ""}`} data-testid={`sh-qty-${r.product_id}-${r.warehouse_id}`}>
                    {r.qty} {r.unit}
                  </TableCell>
                  <TableCell className="text-right text-slate-500">{r.cartons ?? "—"}</TableCell>
                  {showCost && <TableCell className="text-right" data-testid={`sh-cost-${r.product_id}-${r.warehouse_id}`}>₹{r.avg_cost}</TableCell>}
                  {showCost && <TableCell className="text-right font-medium" data-testid={`sh-value-${r.product_id}-${r.warehouse_id}`}>₹{r.value}</TableCell>}
                </TableRow>
              ))}
              <TableRow className="border-t-2 border-slate-200 bg-slate-50 font-semibold" data-testid="sh-totals-row">
                <TableCell colSpan={2}>Total ({rows.length} row{rows.length === 1 ? "" : "s"})</TableCell>
                <TableCell className="text-right" data-testid="sh-total-qty">{Math.round(totalQty * 100) / 100}</TableCell>
                <TableCell />
                {showCost && <TableCell />}
                {showCost && <TableCell className="text-right" data-testid="sh-total-value">₹{Math.round(totalValue * 100) / 100}</TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {showCost && (
        <p className="text-xs text-slate-400" data-testid="sh-cost-note">
          Valued at weighted-average cost replayed from inbound transactions up to the selected date — the average cost as it actually stood on that day.
        </p>
      )}
    </div>
  );
}
